<template>
    <div
        class="px-md gap-xl hide-scrollbar grid w-full grid-cols-8 content-start items-start"
    >
        <form id="info-form" class="gap-lg col-span-5 flex flex-col">
            <h1 class="mb-md">Contract</h1>
            <div class="input-group gap-xs flex flex-col">
                <label for="dateRange" class="required">Contract start date</label>

                <div class="relative rounded-sm border border-(--border-strong)">
                    <DatePicker
                        v-model="contractStart"
                        date-format="dd.M.yy"
                        placeholder="DD.MM.YYYY"
                        class="bg-lightest p-xs w-full rounded-sm pr-5"
                        :disabled="disable"
                        :class="{
                            'text-(--text-disabled)': !contractStart,
                        }"
                    />

                    <i class="i-calendar absolute top-2.5 right-2.5" />
                </div>
            </div>

            <div class="input-group gap-xs flex flex-col">
                <label for="dateRange" class="required">Contract end</label>

                <div class="relative rounded-sm border border-(--border-strong)">
                    <DatePicker
                        v-model="contractEnd"
                        date-format="dd.M.yy"
                        placeholder="DD.MM.YYYY"
                        class="bg-lightest p-xs w-full rounded-sm pr-5"
                        :disabled="disable"
                        :min-date="minEndDate"
                        :class="{
                            'text-(--text-disabled)': !contractEnd,
                        }"
                    />

                    <i class="i-calendar absolute top-2.5 right-2.5" />
                </div>
            </div>

            <div class="input-group">
                <label for="description" class="mb-xs">Description</label>
                <div class="input-wrapper">
                    <textarea
                        id="description"
                        v-model="description"
                        class="h-40"
                        placeholder="Contract description"
                        :disabled="disable"
                    />
                </div>
            </div>

            <div class="gap-sm flex flex-col capitalize">
                <div class="gap-sm flex">
                    <h2>SLA lines</h2>
                    <TagComponent :value="SLALines?.length?.toString()" variant="info" class-name="text-(--state-info-fg)" />
                </div>

                <SLALineComponent :sla-lines="SLALines ?? []" :readonly="disable" @update="update" />
            </div>
        </form>
    </div>

    <BottomButtons
        arrow
        :handle-submit="handleSubmit"
        :disabled="disable || !allowSubmit"
        :sticky="false"
        :loading="loading"
        dont-disable
        @cancel="emit('cancel')"
    />
</template>

<script setup lang="ts">
import DatePicker from 'primevue/datepicker';
import BottomButtons from '@/components/bottom-buttons.vue';
import { onMounted, ref, computed, watch, type PropType } from 'vue';
import type { AdminContract, AdminSLAType } from '@/stores/store-interfaces';
import SLALineComponent from '@/components/admin/sla-lines.vue';
import TagComponent from '@/components/ui/tag/TagComponent.vue';
import { mapSlaTypesToValues } from '@/stores/helpers';

const props = defineProps({
    slatypes: { type: Array as PropType<AdminSLAType[]>, required: true },
    contractData: { type: Object as PropType<AdminContract> },
    disable: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
});
const contractStart = ref<Date | null>(null);
const contractEnd = ref<Date | null>(null);
const description = ref<string>('');
const allowSubmit = computed(() => {
    if (!contractStart.value || !contractEnd.value) {
        return false;
    }
    const startDate = new Date(contractStart.value);
    const endDate = new Date(contractEnd.value);
    const oneMonthInMs = 30 * 24 * 60 * 60 * 1000;
    return endDate.getTime() - startDate.getTime() >= oneMonthInMs;
});
const minEndDate = computed(() => {
    return new Date(
        contractStart.value ? contractStart.value.getFullYear() : 0,
        contractStart.value ? contractStart.value.getMonth() + 1 : 0,
        contractStart.value ? contractStart.value.getDate() : 0,
    );
});

const SLALines = ref<AdminContract['sla']>([]);
const SLAValues = ref<{ sla_type_id: string; sla_tag_id?: string; value: string }[]>([]);

onMounted(() => {
    if (props.contractData) {
        SLALines.value = props.contractData.sla.map((line) => ({
            sla_type: {
                ...line.sla_type,
                unit: line.sla_type.name === 'Notification' ? 'Days' : line.sla_type.unit,
            },
            value: line.value,
            sla_tags: line.sla_tags,
        }));
    } else {
        SLALines.value = props.slatypes.map(mapSlaTypesToValues);
    }
});

watch(
    () => props.slatypes,
    (value) => {
        SLALines.value = value.map(mapSlaTypesToValues);
    },
);

watch(
    () => props.contractData,
    (value) => {
        console.log(value);
        if (value) {
            SLALines.value = value.sla;
        }
    },
);

const update = (data: { sla_type_id: string; sla_tag_id?: string; value: string }[]) => {
    SLAValues.value = data;
};

const emit = defineEmits(['cancel', 'submit']);

const handleSubmit = () => {
    const submitObj = {
        start_date: Math.floor(contractStart.value!.getTime() / 1000),
        end_date: Math.floor(contractEnd.value!.getTime() / 1000),
        description: description.value,
        sla: SLAValues.value,
    };

    emit('submit', submitObj);
};
</script>

<style scoped>
@reference '@/assets/styles/default.css';
p {
    @apply text-md font-default font-thin;
}

label {
    display: flex;
    align-items: center;
}

.required::before {
    content: '*';
    margin-right: var(--spacing-xs);
    color: var(--color-error);
    display: flex;
    align-items: center;
}
</style>
